import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "components/ui/command";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "components/ui/dialog";
import { useTenantSwitcher } from "shared/context/TenantSwitcherState";

// Rendered once per layout (see Layout.tsx) so only one command palette can
// ever be open, even with multiple <TenantSwitcher> trigger buttons mounted.
const TenantSwitcherDialog = () => {
  const {
    hasAccess,
    open,
    closePalette,
    search,
    setSearch,
    filteredTenants,
    onSelectTenant,
    currentTenantName,
  } = useTenantSwitcher();

  if (!hasAccess) {
    return null;
  }

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) {
      closePalette();
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="flex max-h-[calc(100dvh-2rem)] max-w-xl flex-col gap-0 divide-y divide-slate-200 overflow-hidden rounded-xl bg-white p-0 shadow-2xl outline-1 outline-slate-900/5 sm:max-h-[calc(100dvh-3rem)] md:max-h-[calc(100dvh-10rem)]"
        showCloseButton={false}
      >
        <DialogTitle className="sr-only">Switch tenant</DialogTitle>
        <DialogDescription className="sr-only">
          Search and select a tenant.
        </DialogDescription>
        <Command className="h-auto min-h-0 flex-1" shouldFilter={false}>
          <CommandInput
            autoFocus
            aria-label="Search tenants"
            onValueChange={setSearch}
            placeholder="Search tenants..."
            value={search}
          />
          <CommandList className="min-h-0 max-h-none text-slate-800">
            <CommandEmpty>No tenants found.</CommandEmpty>
            <CommandGroup>
              {filteredTenants.map((tenant) => {
                const isCurrent = tenant.tenant.name === currentTenantName;

                return (
                  <CommandItem
                    key={tenant.tenant.id}
                    onSelect={() => onSelectTenant(tenant)}
                    value={tenant.tenant.id}
                  >
                    <div className="flex min-w-0 items-center gap-3">
                      <span
                        className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[10px] font-bold ${
                          isCurrent
                            ? "bg-blue-200 text-blue-800"
                            : "bg-slate-100 text-slate-700 group-data-[selected=true]:bg-blue-100 group-data-[selected=true]:text-blue-700"
                        }`}
                      >
                        {tenant.tenant.name.slice(0, 2).toUpperCase()}
                      </span>
                      <span className="truncate font-medium text-slate-800 group-data-[selected=true]:text-blue-900">
                        {tenant.tenant.name}
                      </span>
                    </div>
                    {isCurrent ? (
                      <span className="rounded-full bg-blue-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-blue-800">
                        Active
                      </span>
                    ) : (
                      <FontAwesomeIcon
                        icon="chevron-right"
                        className="h-3 w-3 text-slate-400 group-data-[selected=true]:text-blue-600"
                      />
                    )}
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </DialogContent>
    </Dialog>
  );
};

export default TenantSwitcherDialog;
